import React, { useEffect, useState, useContext } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { apiService } from "service/ApiService";
import ProductSalesTable from "components/Tables/ProductSalesTable";
import StoreContext from "contexts/StoreContext";
import DateRangeContext from "contexts/DateRangeContext";
import ExportToExcelButton from "views/Components/ExportToExcelButton";
import { exportToExcel } from "utils/AppUtils";
import ExtraSalesTable from "components/Tables/ExtraSalesTable";
import { ClipLoader } from "react-spinners";

import { useTranslation } from "react-i18next";

function ProductStatsPage() {
  const {
    t,
  } = useTranslation();

  const { storeId, setStoreId } = useContext(StoreContext);
  const { dateRange, setDateRange } = useContext(DateRangeContext);
  const [data, setData] = useState([]);
  const [extrasData, setExtrasData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [extraCategories, setExtraCategories] = useState([]);
  const [showLoader, setShowLoader] = useState(true);

  const colsPerRow = 2;
  const rows = [];
  const extrasRows = [];
  for (let i = 0; i < categories.length; i += colsPerRow) {
    rows.push(categories.slice(i, i + colsPerRow));
  }

  for (let i = 0; i < extraCategories.length; i += colsPerRow) {
    extrasRows.push(extraCategories.slice(i, i + colsPerRow));
  }

  const refreshData = () => {
    return apiService
      .post(`api/v1/stores/${storeId}/product-sales`, {
        fromDate: dateRange[0],
        toDate: dateRange[1],
      })
      .then((resp) => {
        setData(resp);
        setCategories([...new Set(resp.map((item) => item.category_name))]);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const refreshExtrasData = () => {
    return apiService
      .post(`api/v1/stores/${storeId}/extras-sales`, {
        fromDate: dateRange[0],
        toDate: dateRange[1],
      })
      .then((resp) => {
        setExtrasData(resp);
        setExtraCategories([...new Set(resp.map((item) => item.extraCategoryId))]);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const formatData = (data) => {
    if (data.length === 0) return [];
    let totalQuantity = 0;
    let totalSummary = 0;
    let formattedData = data.map((row) => {
      totalQuantity += row.quantity;
      totalSummary += row.total;
      return {
        ID: row.product_id,
        [t("tables.headers.product")]: row.product_name,
        [t("tables.headers.category")]: row.category_name,
        [t("tables.headers.quantity")]: Number(row.quantity),
        [t("tables.headers.totalEuro")]: row.total,
      };
    });

    formattedData.push({
      ID: null,
      [t("tables.headers.product")]: t("tables.headers.total"),
      [t("tables.headers.category")]: null,
      [t("tables.headers.quantity")]: totalQuantity,
      [t("tables.headers.totalEuro")]: totalSummary.toFixed(2),
    });

    return formattedData;
  };

  const handleExportData = () => {
    exportToExcel(formatData(data), `product-sales-${+new Date()}`, "Product Sales");
  };

  useEffect(() => {
    if (!storeId || !dateRange) return;
    setShowLoader(true);
    Promise.allSettled([refreshData(), refreshExtrasData()]).finally(() => setShowLoader(false));
  }, [storeId, dateRange]);

  return (
    <>
      <Container fluid>
        <Row>
          <Col md="12" xs="12">
            <ExportToExcelButton handler={handleExportData} disabled={data.length === 0} />
          </Col>
        </Row>
        {showLoader ? (
          <div style={{ textAlign: "center", marginTop: "5vh", marginBottom: "5vh" }}>
            <ClipLoader size={50} color={"#ff6fbc"} />
          </div>
        ) : (
          <>
            {rows.map((row, rowIndex) => (
              <Row key={rowIndex}>
                {row.map((category, colIndex) => (
                  <Col key={colIndex} xs={12} md={12}>
                    <ProductSalesTable categoryName={category} data={data} />
                  </Col>
                ))}
              </Row>
            ))}
            {extrasRows.map((row, rowIndex) => (
              <Row key={rowIndex}>
                {row.map((extraCategoryId, colIndex) => (
                  <Col key={colIndex} xs={12} md={12}>
                    <ExtraSalesTable categoryId={extraCategoryId} data={extrasData} />
                  </Col>
                ))}
              </Row>
            ))}
          </>
        )}
      </Container>
    </>
  );
}

export default ProductStatsPage;
